/**
 * Jovii — PLAYER, full screen, which is three different APIs wearing one
 * button.
 *
 * ── WHY THIS FILE EXISTS ───────────────────────────────────────────────────────
 *
 * The button did nothing at all on an iPhone. The code called
 * `element.requestFullscreen()`, caught the failure and stayed inline, with a
 * comment saying iOS would need `webkitEnterFullscreen`. That comment was right and
 * unimplemented, so the owner pressed a control that had no effect on the device he
 * watches on.
 *
 * There are exactly three routes, and which ones exist depends on the platform:
 *
 * | route            | what it is                          | where |
 * |------------------|-------------------------------------|-------|
 * | `standard`       | `Element.requestFullscreen()`       | everywhere except iPhone |
 * | `webkit-element` | `Element.webkitRequestFullscreen()` | older Safari, iPadOS |
 * | `webkit-video`   | `video.webkitEnterFullscreen()`     | **iPhone, and only this** |
 *
 * The first two fullscreen an ELEMENT — our stage — so the control bar, the
 * subtitles and the floating call window come with it. The third hands the video
 * over to the platform's own player: a full-screen AVPlayer-style UI with Apple's
 * controls, and **none of our DOM is on screen while it is up**. That is not a
 * choice we get to make; on iPhone it is fullscreen or nothing.
 *
 * ── THE PROBE HAS TO BE ASKED LATE ─────────────────────────────────────────────
 *
 * `video.webkitSupportsFullscreen` is FALSE until the element has a `MediaPlayer`
 * (`HTMLVideoElement::supportsFullscreen()` opens with
 * `if (!page || !player()) return false;`), and ours gets one only when the media
 * engine attaches its MediaSource. Measured here, in WebKit (GStreamer, so NOT iOS,
 * but the same C++): `false` on a fresh element, `true` 1.5 s after a source was
 * attached. That is the same bail-out `webkitSupportsPresentationMode` goes through
 * on iOS, and it is why the Picture-in-Picture button never appeared there.
 *
 * So nothing is cached. The route is chosen at the moment the button is pressed,
 * when the film is playing and every probe tells the truth.
 */

interface WebkitDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface WebkitElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface WebkitVideo extends HTMLVideoElement {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitSupportsFullscreen?: boolean;
  webkitDisplayingFullscreen?: boolean;
}

export type FullscreenRoute = 'standard' | 'webkit-element' | 'webkit-video' | 'none';

const doc = (): WebkitDocument | null =>
  typeof document === 'undefined' ? null : (document as WebkitDocument);

/** Keep the rotation child, loading UI, chat and call layer inside fullscreen. */
export function fullscreenTarget(stage: HTMLElement | null): HTMLElement | null {
  return stage?.closest?.<HTMLElement>('[data-watch-viewport]') ?? stage;
}

/**
 * Which route this platform has, best first.
 *
 * "Best" means "keeps our own chrome": an element fullscreen paints the control
 * bar, the subtitles and the call window; the video's own does not.
 */
export function fullscreenRoute(
  stage: HTMLElement | null,
  video: HTMLVideoElement | null
): FullscreenRoute {
  stage = fullscreenTarget(stage);
  const d = doc();
  if (d === null) return 'none';
  const el = stage as WebkitElement | null;
  if (el !== null && typeof el.requestFullscreen === 'function' && d.fullscreenEnabled !== false) {
    return 'standard';
  }
  if (
    el !== null &&
    typeof el.webkitRequestFullscreen === 'function' &&
    d.webkitFullscreenEnabled !== false
  ) {
    return 'webkit-element';
  }
  // iPhone. `webkitSupportsFullscreen` is not consulted here — see the header: it
  // answers `false` until the element has a player, and this is asked at the moment
  // of the press, when it does. A refusal is caught below.
  if (video !== null && typeof (video as WebkitVideo).webkitEnterFullscreen === 'function') {
    return 'webkit-video';
  }
  return 'none';
}

/** Is anything of ours full screen right now, by any of the three routes? */
export function fullscreenActive(
  stage: HTMLElement | null,
  video: HTMLVideoElement | null
): boolean {
  stage = fullscreenTarget(stage);
  const d = doc();
  if (d === null) return false;
  if (stage !== null && d.fullscreenElement === stage) return true;
  if (stage !== null && d.webkitFullscreenElement === stage) return true;
  return (video as WebkitVideo | null)?.webkitDisplayingFullscreen === true;
}

/**
 * Go full screen. Returns the route that actually worked, or `none`.
 *
 * NEVER THROWS. Every caller is a button or a key, and a browser that refuses
 * (no user gesture, an iframe without `allowfullscreen`, an element with no player
 * yet) must leave the film playing inline rather than take anything down.
 */
export async function enterFullscreen(
  stage: HTMLElement | null,
  video: HTMLVideoElement | null,
  allowNativeVideo = true
): Promise<FullscreenRoute> {
  stage = fullscreenTarget(stage);
  const route = fullscreenRoute(stage, video);
  if (route === 'webkit-video' && !allowNativeVideo) return 'none';
  try {
    if (route === 'standard') {
      await (stage as HTMLElement).requestFullscreen();
      return 'standard';
    }
    if (route === 'webkit-element') {
      await (stage as WebkitElement).webkitRequestFullscreen?.();
      return 'webkit-element';
    }
    if (route === 'webkit-video') {
      (video as WebkitVideo).webkitEnterFullscreen?.();
      return 'webkit-video';
    }
  } catch {
    /* refused; stay inline */
  }
  return 'none';
}

/** Leave, whichever way we got in. Also never throws. */
export async function exitFullscreen(
  stage: HTMLElement | null,
  video: HTMLVideoElement | null
): Promise<void> {
  stage = fullscreenTarget(stage);
  const d = doc();
  if (d === null) return;
  try {
    if ((video as WebkitVideo | null)?.webkitDisplayingFullscreen === true) {
      (video as WebkitVideo).webkitExitFullscreen?.();
      return;
    }
    if (stage !== null && d.fullscreenElement === stage) {
      await d.exitFullscreen();
      return;
    }
    if (stage !== null && d.webkitFullscreenElement === stage) {
      await d.webkitExitFullscreen?.();
    }
  } catch {
    /* already out, or refused */
  }
  void stage;
}

/**
 * Landscape, on a phone that will listen.
 *
 * Absent on desktops and on iOS, where the platform decides; a rejection here is
 * ordinary and must not colour the fullscreen call's result.
 */
export async function lockLandscape(): Promise<boolean> {
  try {
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (o: string) => Promise<void>;
    };
    if (typeof orientation?.lock !== 'function') return false;
    await orientation.lock('landscape');
    return true;
  } catch {
    return false;
  }
}

/** Safe on browsers without the Screen Orientation API, including iOS Safari. */
export function unlockOrientation(): void {
  try {
    screen.orientation?.unlock?.();
  } catch {
    /* unsupported, detached document, or already unlocked */
  }
}

/**
 * Subscribe to entering and leaving, across all three routes.
 *
 * The video-element route fires its own pair (`webkitbeginfullscreen` /
 * `webkitendfullscreen`) and NOTHING on the document — so a listener on
 * `fullscreenchange` alone leaves the button showing "Full screen" while the
 * platform's player is up, and showing the wrong icon on the way back.
 */
export function onFullscreenChange(
  stage: HTMLElement | null,
  video: HTMLVideoElement | null,
  fn: (active: boolean) => void
): () => void {
  const report = (): void => fn(fullscreenActive(stage, video));
  const d = doc();
  const docEvents = ['fullscreenchange', 'webkitfullscreenchange'];
  const vidEvents = ['webkitbeginfullscreen', 'webkitendfullscreen'];
  if (d !== null) for (const e of docEvents) d.addEventListener(e, report);
  if (video !== null) for (const e of vidEvents) video.addEventListener(e, report);
  return () => {
    if (d !== null) for (const e of docEvents) d.removeEventListener(e, report);
    if (video !== null) for (const e of vidEvents) video.removeEventListener(e, report);
  };
}
